<script lang="ts" setup>
import type { CartItemRead } from '@shared/types'
import categories from '@/data/categories'

import { useStore } from '@/store'
import {
	Avatar,
	Btn,
	Card,
	Dropdown,
	Icon,
	ListItem,
	TextInput,
	pathKeyToURL,
	useDevice,
} from '@bagelink/vue'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import events from '@/data/events'

const store = useStore()

const { scrollY, innerWidth } = useDevice()
const scrolledMenu = computed(() => scrollY.value > 60)
const openMobileMenu = ref(false)
const openCartDropdown = ref(false)
const animateCartCount = ref(false)
const route = useRoute()
const router = useRouter()
const openSearch = ref(innerWidth.value < 1160)
let searchText = ref('')

watch(() => innerWidth.value, (val) => {
	if (val >= 1160) {
		openSearch.value = false
	}
})

function runSearch() {
	router.push({ path: '/search', query: { q: searchText.value } })
}

let timeout: any = null

function showCartUpdate() {
	if (route.path === '/cart' || store.cart?.item_count === 0){
		openCartDropdown.value = false
		 return}
	openCartDropdown.value = true
	resetCartTimeout()
}

function resetCartTimeout() {
	if (timeout) clearTimeout(timeout)
	timeout = setTimeout(() => (openCartDropdown.value = false), 4000)
}

function handleCartMouseLeave() {
	resetCartTimeout()
}

function clickOutside() {
	openCartDropdown.value = false
}

function handleCartMouseEnter() {
	if (!store.cart || store.cart.item_count === 0 || route.path === '/cart') return
	openCartDropdown.value = true
	resetCartTimeout()
}

function cartItemString(item: CartItemRead) {
	let result = ''
	if (item.quantity > 1) result = `✕ ${item.quantity.toLocaleString()} - `
	result += `${item.product.price.toLocaleString()}₪`
	return result
}

// פונקציה להסרת מוצר מהעגלה
// function removeCartItem(item: CartItemRead) {
// 	if (typeof store.removeFromCart === 'function') {
// 		store.removeFromCart(item.product.id)
// 	}
// }

watch(() => store.cart?.items, showCartUpdate)

watch(route, () => {
	openMobileMenu.value = false
	if (route.path === '/cart') {
		openCartDropdown.value = false
	}
})

watch(
	() => store.cart?.item_count,
	(newVal, oldVal) => {
		if (oldVal !== undefined && newVal !== oldVal) {
			animateCartCount.value = true
			setTimeout(() => (animateCartCount.value = false), 700)
		}
	}
)

const links = [
	{ title: 'פודקאסטים', to: '/#podcast', visible: ()=> true },
	{ title: 'אירועים', to: '/event', visible: ()=> events.length > 0 },
	{ title: 'אודות', to: '/about', visible: ()=> true },
]

const featuredCategories = computed(() => categories.filter(c => c.featured))
</script>

<template>
	<div class="flex p-1 m_py-025 w-100 align-items-center sticky transition z-99 gap-05 m_gap-025 darkMenu shadow-30" :class="{ scrolledMenu }">
		<router-link to="/" thin flat class="menuLogo me-auto txt-center decoration-none">
			<img src="@/assets/logo.svg" alt="logo">
			<p class="txt14 bold pt-025">
				רעיונות פורצי דרך
			</p>
		</router-link>
		<div class="mobileMenu m_bg-black m_fixed m_z-99 m_vh-100 m_top m_end m_w-100vw m_flex m_column transition" :class="{ openMobileMenu }">
			<div class="flex justify-content-end p-025 none m_block">
				<Btn icon="close" class="txt40" size="medium" @click="openMobileMenu = false" />
			</div>
			<div class="flex m_column gap-05 txt-16 loadingLinks">
				<div class="flex searchWrap justify-content-end m_justify-content-center navLink">
					<Btn v-if="!openSearch" flat class="navLink px-05 txt-16 relative radius-05 rotate-0" icon="search" @click="openSearch = !openSearch" />
					<Btn v-if="openSearch" flat class="navLink px-05 txt-16 relative radius-05 rotate-0 closeSearch" icon="close" @click="openSearch = false" />
					<transition name="SearchTransition">
						<form v-if="openSearch" class="relative" @submit.prevent="runSearch">
							<TextInput v-model="searchText" placeholder="חיפוש" class="m-0 bg-input-gray-20" />
							<div class="absolute end-05 top-0 h-100p flex column justify-content-center">
								<Btn icon="search" thin type="submit" />
							</div>
						</form>
					</transition>
				</div>
				<Dropdown value="ספרים" :disabled="!featuredCategories.length">
					<template #trigger>
						<Btn to="/products" flat class="navLink px-05 txt-16 relative radius-05 block" value="ספרים" :iconEnd="featuredCategories.length ? 'keyboard_arrow_down' : undefined" />
					</template>
					<div class="px-1 py-05">
						<ListItem v-for="category in featuredCategories" class="navLink px-05 txt-16 relative radius-05 block" :title="category.name" @click="() => router.push(`/products?category=${category.name}`)" />
					</div>
				</Dropdown>
				<Btn v-for="link in links" :key="link.title" :to="link.to" flat class="navLink px-05 m_mx-2 txt-16 relative radius-05" :value="link.title" v-show="link.visible()" />
				<Btn class="navLink" href="/shibolet" value="הצטרפו לקהילת המנויים" />
			<!-- <Btn to="/login" flat outline class="navLink px-1 m_mx-2 txt-16 relative" value="התחברות" /> -->
			</div>
		</div>
		<div
			v-click-outside="clickOutside" class="relative cart-container"
			tabindex="0"
			@mouseenter="handleCartMouseEnter"
			@mouseleave="handleCartMouseLeave"
			@focusin="handleCartMouseEnter"
			@focusout="handleCartMouseLeave"
		>
			<RouterLink to="/cart" class="txt-center txt-16 decoration-none  hover px-1 rounded flex py-05 color-white gap-05 relative" :class="{ 'animate-bounce': animateCartCount }">
				<Icon name="shopping_cart" size="1.6" />
				<div v-if="store.cart?.item_count" class="white-space absolute bg-red round notificationCount">
					{{ store.cart.item_count }}
				</div>
			</RouterLink>
			<Card v-if="openCartDropdown" thin class="absolute end txt-black z-999 mt-1 shadow-30 cartCard grid overflow">
				<div>
					<div
						v-for="item in store.cart?.items"
						:key="item.id"
						class="grid grid-wrap-6 m_grid-wrap-6 gap-025 align-items-center scrollbar-gutter-stable-both mb-05"
						tabindex="0"
						@mouseenter="handleCartMouseEnter"
						@mouseleave="handleCartMouseLeave"
						@focusin="handleCartMouseEnter"
						@focusout="handleCartMouseLeave"
					>
						<Avatar class="radius-1" :src="pathKeyToURL(item.product.image_path)" :size="40" />
						<p class="grid-span-4 ellipsis-1 line-height-2">
							{{ item.product.title }}
						</p>
						<p class="opacity-7 txt12 white-space">
							{{ cartItemString(item) }}
						</p>
					</div>
				</div>
				<Btn to="/cart" color="gray" class="block mt-075" value="להשלמת הרכישה" />
			</Card>
		</div>
		<Btn class="none m_block transition" icon="menu" round flat @click="openMobileMenu = true" />
	</div>
</template>

<style>
.cartCard {
	min-width: 270px !important;
	grid-template-rows: 1fr 50px;
	max-height: 300px;
	overflow-y: auto;
}

.SearchTransition-enter-active {
	transition: all 0.3s ease;
}

.SearchTransition-leave-active {
	transition: all 0.3s ease;
}

.SearchTransition-enter,
.SearchTransition-leave-to {
	opacity: 0;
	transform: translateX(-10px);
}

.searchWrap input {
	width: 290px !important;
	min-width: 190px !important;
	color: white !important;
}

.searchWrap input::placeholder {
	color: rgba(255, 255, 255, 0.279) !important;
}
.notificationCount{
	width: 1.25rem;
	height: 1.25rem;
	font-size: 12px;
	transform: translate(0.75rem, -0.75rem);
}

@media screen and (max-width: 1160px) {
	.searchWrap input {
		width: 100% !important;
		min-width: 50vw !important;
	}
}
</style>

<style scoped>
.menuLogo {
	width: 170px;
	padding-bottom: 0.45rem;
}

.darkMenu {
	max-width: 1600px;
	width: 94%;
	margin: 0rem auto 1rem;
	padding: 0.5rem 1.5rem !important;
	border-radius: 1.25rem;
	top: 1rem;
}

.scrolledMenu,
.darkMenu {
	background: var(--bgl-black);
	--bgl-text-color: var(--bgl-white);
	--bgl-transition-400: all 100ms ease;
}

.scrolledMenu .menuLogo,
.darkMenu .menuLogo {
	filter: brightness(0) invert(1);
}

.scrolledMenu .navLink,
.darkMenu .navLink {
	color: var(--bgl-white);
}

.mobileMenu .router-link-active.router-link-exact-active.bgl_btn::after {
	content: '';
	position: absolute;
	width: 80%;
	margin: 0 auto;
	height: 2px;
	background: var(--bgl-primary);
	inset: 0;
	top: auto;
}

.animate-bounce {
	animation: bounce 0.2s cubic-bezier(0.6, -0.28, 0.735, 0.045) forwards;
}

@keyframes bounce {

	0%,
	100% {
		transform: translateY(0);
	}

	50% {
		transform: translateY(-2px);
	}
}

.animate-bounce .bgl_icon-font {
	animation: cute-bounce 0.8s cubic-bezier(0.33, 1.5, 0.68, 1) forwards;
	transform-origin: center center;
}

@keyframes cute-bounce {

	0%,
	100% {
		transform: scale(1);
	}

	30%,
	70% {
		transform: scale(1.2);
	}

	50% {
		transform: scale(0.95);
	}
}

@media screen and (max-width: 1160px) {
	.loadingLinks {
		gap: 0 !important;
		flex-wrap: wrap;
		justify-content: end;
		--input-height: 36px;
	}

	.closeSearch {
		display: none;
	}

	.searchWrap {
		width: 100%;
		flex-grow: 1;
	}
}

@media screen and (max-width: 910px) {
	.menuLogo {
		width: 120px;
	}

	.darkMenu {
		padding: 0.5rem 1rem !important;
	}

	.mobileMenu {
		min-width: 100vw;
		transform: translateX(-105%);
		transition: var(--bgl-transition);
	}

	.mobileMenu * {
		--btn-flat-color: var(--bgl-white) !important;
		color: var(--bgl-white) !important;
	}

	.openMobileMenu {
		transform: translateX(0%);
		transition: var(--bgl-transition);
	}

	.loadingLinks .navLink {
		opacity: 0;
	}

	.openMobileMenu .loadingLinks .navLink {
		transform: translateY(10px);
		animation: fadeInLinks 0.5s ease forwards;
	}

	@keyframes fadeInLinks {
		from {
			opacity: 0;
			transform: translateY(10px);
		}

		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	.loadingLinks .navLink:nth-child(1) {
		animation-delay: 100ms;
	}

	.loadingLinks .navLink:nth-child(2) {
		animation-delay: 200ms;
	}

	.loadingLinks .navLink:nth-child(3) {
		animation-delay: 300ms;
	}

	.loadingLinks .navLink:nth-child(4) {
		animation-delay: 400ms;
	}

	.loadingLinks .navLink:nth-child(5) {
		animation-delay: 500ms;
	}

	.loadingLinks .navLink:nth-child(6) {
		animation-delay: 600ms;
	}

	.loadingLinks .navLink:nth-child(7) {
		animation-delay: 700ms;
	}

	.loadingLinks .navLink:nth-child(8) {
		animation-delay: 800ms;
	}

	.loadingLinks .navLink:nth-child(9) {
		animation-delay: 900ms;
	}

	.loadingLinks .navLink:nth-child(10) {
		animation-delay: 1000ms;
	}
}
</style>
